import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET, PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';
import ApiCaller from '../../../../api/ApiCaller';
import ResponseParametersForm from '../components/ResponseParametersForm';

const AggregationAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [tableColumnsLoading, setTableColumnsLoading] = useState(false);
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [requestParameters, setRequestParameters] = useState([]);
    const [allRequestParameters, setAllRequestParameters] = useState([]);
    const [requestParamSource, setRequestParamSource] = useState(formData?.requestParamSource || ['custom_request']);
    const [responseParameters, setResponseParameters] = useState(formData?.responseParameters || []);
    const [aggregationQuery, setAggregationQuery] = useState('');
    const [formattedPreview, setFormattedPreview] = useState('');
    const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
    const [parametersLoading, setParametersLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [columnAttributes, setColumnAttributes] = useState({});

    // Helper Functions
    const getParameterSourcePrefix = (parameter) => {
        if (!parameter) return '';
        if (parameter.label?.includes('Custom Request')) return 'custom_request';
        const stepMatch = parameter.label?.match(/Step (\d+)/);
        if (stepMatch) return `step_${stepMatch[1]}`;
        return 'custom_request';
    };

    const parseMappingString = (mappingValue) => {
        const mappingParts = mappingValue.split('.');
        return {
            sourcePrefix: mappingParts[0] || '',
            parameterName: mappingParts[1] || '',
            attributeName: mappingParts[2] || ''
        };
    };

    const buildMappingString = (sourcePrefix, parameterName, attributeName = '') => {
        const baseMapping = `${sourcePrefix}.${parameterName}`;
        return attributeName ? `${baseMapping}.${attributeName}` : baseMapping;
    };

    const getParameterAttributes = (parameter) => {
        if (!parameter) return [];
        
        if (parameter.value && typeof parameter.value === 'object' && parameter.value.config?.attributes) {
            return parameter.value.config.attributes || [];
        }
        
        const foundParameter = allRequestParameters?.find(param => param?.id === parameter?.value);
        
        if (foundParameter?.type === 'OBJECT') {
            return foundParameter?.config?.attributes || foundParameter?.attributes || [];
        }
        
        return foundParameter?.config?.attributes || [];
    };

    // Effects
    useEffect(() => {
        if (formData?.tableId) {
            fetchTableAttributes(formData.tableId);
        }
    }, [formData?.tableId]);

    useEffect(() => {
        if (formData?.aggregationQuery) {
            setAggregationQuery(formData.aggregationQuery);
        }
        if (formData?.mappings) {
            // Handle existing mappings
        }
    }, [formData?.aggregationQuery, formData?.mappings]);

    // Sync existing response parameters from formData (edit mode)
    useEffect(() => {
        if (Array.isArray(formData?.responseParameters)) {
            setResponseParameters(formData.responseParameters);
        }
    }, [formData?.responseParameters]);

    // Auto-load request parameters when selectedRequest changes
    useEffect(() => {
        if (selectedRequest && requestParamSource.includes('custom_request')) {
            fetchRequestParameters(selectedRequest);
        }
    }, [selectedRequest]);

    useEffect(() => {
        // Clear existing parameters when sources change
        setRequestParameters([]);
        setAllRequestParameters([]);
        
        // Load parameters from all selected sources
        requestParamSource.forEach(source => {
            if (source === 'custom_request') {
                // Load request parameters when request is selected
                if (selectedRequest) {
                    fetchRequestParameters(selectedRequest);
                }
            } else {
                loadStepParameters(source);
            }
        });
    }, [requestParamSource, selectedRequest, allPreviousSteps]);

    useEffect(() => {
        generatePreview();
    }, [aggregationQuery, formData?.tableId, formData?.mappings]);

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        
        if (name === 'tableId') {
            fetchTableAttributes(value);
        }
    };

    const handleQueryChange = (e) => {
        const query = e.target.value;
        setAggregationQuery(query);
        setFormData(prev => ({ ...prev, aggregationQuery: query }));
    };

    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
        }));
    };

    const handleMappingChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            mappings: {
                ...prev.mappings,
                [fieldName]: value
            }
        }));
    };

    const handleParameterMappingChange = (selected, placeholder) => {
        const currentMappings = { ...formData.mappings } || {};
        
        if (currentMappings[placeholder]) {
            delete currentMappings[placeholder];
        }
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${placeholder}.`)) {
                delete currentMappings[key];
            }
        });
        
        if (!selected) {
            setColumnAttributes(prev => {
                const newAttributes = { ...prev };
                delete newAttributes[placeholder];
                return newAttributes;
            });
        } else {
            const sourcePrefix = getParameterSourcePrefix(selected);
            const parameterName = selected.label.split(' ')[0];
            const parameterMapping = buildMappingString(sourcePrefix, parameterName);
            
            const selectedColumnAttribute = columnAttributes[placeholder];
            
            if (selectedColumnAttribute) {
                const mappingKey = `${placeholder}.${selectedColumnAttribute}`;
                currentMappings[mappingKey] = parameterMapping;
            } else {
                currentMappings[placeholder] = parameterMapping;
            }
        }
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleAttributeMappingChange = (selected, placeholder) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        let mappingKey = placeholder;
        let mappingValue = currentMappings[placeholder] || '';
        
        const columnAttributeMappings = Object.keys(currentMappings).filter(key => key.startsWith(`${placeholder}.`));
        if (columnAttributeMappings.length > 0) {
            mappingKey = columnAttributeMappings[0];
            mappingValue = currentMappings[mappingKey] || '';
        }
        
        const { sourcePrefix, parameterName } = parseMappingString(mappingValue);
        currentMappings[mappingKey] = buildMappingString(sourcePrefix, parameterName, attributeName);
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleColumnAttributeChange = (selected, placeholder) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        let existingParameterMapping = currentMappings[placeholder];
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${placeholder}.`)) {
                existingParameterMapping = currentMappings[key];
            }
        });
        
        if (currentMappings[placeholder]) {
            delete currentMappings[placeholder];
        }
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${placeholder}.`)) {
                delete currentMappings[key];
            }
        });
        
        if (existingParameterMapping && attributeName) {
            const newKey = `${placeholder}.${attributeName}`;
            currentMappings[newKey] = existingParameterMapping;
        } else if (existingParameterMapping) {
            currentMappings[placeholder] = existingParameterMapping;
        }
        
        setColumnAttributes(prev => ({
            ...prev,
            [placeholder]: attributeName
        }));
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleAddMapping = () => {
        if (selectedPlaceholder && !formData?.mappings?.[selectedPlaceholder]) {
            handleMappingChange(selectedPlaceholder, '');
            setSelectedPlaceholder(null);
        }
    };

    const handleRemoveMapping = (placeholder) => {
        const newMappings = { ...formData?.mappings };
        delete newMappings[placeholder];
        
        // Also remove any attribute mappings
        Object.keys(newMappings).forEach(key => {
            if (key.startsWith(`${placeholder}.`)) {
                delete newMappings[key];
            }
        });
        
        setFormData(prev => ({
            ...prev,
            mappings: newMappings
        }));
        
        // Remove from column attributes
        setColumnAttributes(prev => {
            const newAttributes = { ...prev };
            delete newAttributes[placeholder];
            return newAttributes;
        });
    };

    // API Functions
    const fetchTableAttributes = (tableId) => {
        setTableColumnsLoading(true);
        const requestBody = {
            page: 0,
            size: 100,
            searchFilters: [],
            sortRules: [],
        };
        const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', tableId);

        ApiCaller.callPostApi(
            apiUrl,
            requestBody,
            {},
            (response) => {
                const formAttributes = response?.data?.filter(item => item.formAttribute === true) || [];
                setAllTableColumns(formAttributes);
                setTableColumnsLoading(false);
            },
            (error) => {
                console.error('Error fetching table attributes:', error);
                setAllTableColumns([]);
                setTableColumnsLoading(false);
                // Don't show error to user if it's just a connection issue
                if (error?.message?.includes('ECONNREFUSED')) {
                    console.log('Backend server not available, skipping table attributes fetch');
                } else {
                    toast.error('Failed to fetch table attributes');
                }
            }
        );
    };

    const fetchRequestParameters = (requestId) => {
        if (!requestId) return;
        
        setParametersLoading(true);
        const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', requestId);
        
        ApiCaller.callPostApi(
            apiUrl,
            null,
            {},
            (response) => {
                if (response?.data?.length > 0) {
                    const customRequestParams = [];
                    
                    response.data.forEach((item) => {
                        // Add the main parameter
                        customRequestParams.push({
                            value: item.id,
                            label: `${item.name} (Custom Request)`,
                            type: item.type,
                            source: 'custom_request'
                        });
                        
                        // If it's an object type, add its attributes
                        if (item.type === 'OBJECT' && item.config?.attributes) {
                            item.config.attributes.forEach(attr => {
                                customRequestParams.push({
                                    value: `${item.id}.${attr.name}`,
                                    label: `${item.name}.${attr.name} (Custom Request)`,
                                    type: attr.type,
                                    source: 'custom_request',
                                    parentId: item.id
                                });
                            });
                        }
                    });

                    const stepParams = getStepParameters();
                    setRequestParameters([...customRequestParams, ...stepParams]);
                    setAllRequestParameters(response.data);
                } else {
                    setRequestParameters(getStepParameters());
                }
                setParametersLoading(false);
            },
            (error) => {
                console.error('Error fetching request parameters:', error);
                setRequestParameters(getStepParameters());
                setParametersLoading(false);
                // Don't show error to user if it's just a connection issue
                if (error?.message?.includes('ECONNREFUSED')) {
                    console.log('Backend server not available, using default parameters for testing');
                    // Add some default parameters for testing
                    const defaultParams = [
                        { value: 'customerId', label: 'customerId (Custom Request)', type: 'STRING', source: 'custom_request' },
                        { value: 'priceThreshold', label: 'priceThreshold (Custom Request)', type: 'NUMBER', source: 'custom_request' },
                        { value: 'status', label: 'status (Custom Request)', type: 'STRING', source: 'custom_request' },
                        { value: 'customer.id', label: 'customer.id (Custom Request)', type: 'STRING', source: 'custom_request' },
                        { value: 'customer.name', label: 'customer.name (Custom Request)', type: 'STRING', source: 'custom_request' }
                    ];
                    setRequestParameters(prev => {
                        const existing = prev.filter(p => p.source !== 'custom_request');
                        return [...existing, ...defaultParams];
                    });
                } else {
                    toast.error('Failed to fetch request parameters');
                }
            }
        );
    };

    const loadStepParameters = (source) => {
        // Handle step_ prefix
        const stepId = source.startsWith('step_') ? source.replace('step_', '') : source;
        const stepAttributes = allPreviousSteps?.find(step => step.id === parseInt(stepId))?.action?.config?.responseParameters;
        const stepParams = [];
        
        if (stepAttributes) {
            stepAttributes.forEach(param => {
                // Add the main parameter
                stepParams.push({
                    value: param.name || param,
                    label: `${param?.name || param} (Step ${stepId})`,
                    type: param.type,
                    source: `step_${stepId}`
                });
                
                // If it's an object type, add its attributes
                if (param.type === 'OBJECT' && param.config?.attributes) {
                    param.config.attributes.forEach(attr => {
                        stepParams.push({
                            value: `${param.name}.${attr.name}`,
                            label: `${param.name}.${attr.name} (Step ${stepId})`,
                            type: attr.type,
                            source: `step_${stepId}`,
                            parentId: param.name
                        });
                    });
                }
            });
        }
        
        setRequestParameters(prev => {
            const existing = prev.filter(p => p.source !== `step_${stepId}`);
            return [...existing, ...stepParams];
        });
    };

    const getStepParameters = () => {
        let stepParams = [];
        requestParamSource.forEach(source => {
            if (source !== 'custom_request') {
                // Handle step_ prefix
                const stepId = source.startsWith('step_') ? source.replace('step_', '') : source;
                const stepAttributes = allPreviousSteps?.find(step => step.id === parseInt(stepId))?.action?.config?.responseParameters;
                if (stepAttributes) {
                    stepAttributes.forEach(param => {
                        // Add the main parameter
                        stepParams.push({
                            value: param.name || param,
                            label: `${param?.name || param} (Step ${stepId})`,
                            type: param.type,
                            source: `step_${stepId}`
                        });
                        
                        // If it's an object type, add its attributes
                        if (param.type === 'OBJECT' && param.config?.attributes) {
                            param.config.attributes.forEach(attr => {
                                stepParams.push({
                                    value: `${param.name}.${attr.name}`,
                                    label: `${param.name}.${attr.name} (Step ${stepId})`,
                                    type: attr.type,
                                    source: `step_${stepId}`,
                                    parentId: param.name
                                });
                            });
                        }
                    });
                }
            }
        });
        return stepParams;
    };

    // Preview Generation with placeholder replacement
    const generatePreview = () => {
        if (!aggregationQuery.trim()) {
            setFormattedPreview('');
            return;
        }

        try {
            // Replace placeholders in the query
            let processedQuery = aggregationQuery;
            if (formData?.mappings) {
                Object.entries(formData.mappings).forEach(([placeholder, value]) => {
                    if (value) {
                        // Find the parameter by id or name
                        let parameter = allRequestParameters.find(p => p.id === value || p.name === value);
                        
                        // If not found, check if it's an object attribute (format: parentId.attributeName)
                        if (!parameter && value.includes('.')) {
                            const [parentId, attributeName] = value.split('.');
                            const parentParam = allRequestParameters.find(p => p.id === parentId || p.name === parentId);
                            if (parentParam && parentParam.config?.attributes) {
                                const attr = parentParam.config.attributes.find(a => a.name === attributeName);
                                if (attr) {
                                    parameter = { name: `${parentParam.name}.${attributeName}`, type: attr.type };
                                }
                            }
                        }
                        
                        if (parameter) {
                            // Escape special regex characters in placeholder for safe replacement
                            const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const regex = new RegExp(`\\{${escapedPlaceholder}\\}`, 'g');
                            processedQuery = processedQuery.replace(regex, parameter.name || parameter);
                        }
                    }
                });
            }

            // Try to parse and format the JSON/JavaScript
            let formatted = processedQuery;

            // Check if it's JSON and format it
            if (processedQuery.trim().startsWith('{') || processedQuery.trim().startsWith('[')) {
                try {
                    const parsed = JSON.parse(processedQuery);
                    formatted = JSON.stringify(parsed, null, 2);
                } catch (e) {
                    // If not valid JSON, format as JavaScript
                    formatted = formatJavaScript(processedQuery);
                }
            } else {
                formatted = formatJavaScript(processedQuery);
            }

            setFormattedPreview(formatted);
        } catch (error) {
            setFormattedPreview(aggregationQuery);
        }
    };

    const formatJavaScript = (code) => {
        return code
            .replace(/\s+/g, ' ')
            .replace(/\s*{\s*/g, ' {\n  ')
            .replace(/\s*}\s*/g, '\n}')
            .replace(/;\s*/g, ';\n  ')
            .replace(/,\s*/g, ', ')
            .replace(/\s*\(\s*/g, ' (')
            .replace(/\s*\)\s*/g, ') ')
            .replace(/\s*\[\s*/g, ' [')
            .replace(/\s*\]\s*/g, '] ')
            .replace(/\s*=>\s*/g, ' => ')
            .replace(/\s*:\s*/g, ': ')
            .replace(/\s*=\s*/g, ' = ')
            .trim();
    };

    // Extract placeholders from query
    const extractPlaceholders = (query) => {
        // Regex to capture only valid variable names (alphanumeric and underscores) wrapped in curly braces
        const placeholderRegex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
        const placeholders = [];
        let match;

        while ((match = placeholderRegex.exec(query)) !== null) {
            // Trim whitespace and add to placeholders
            const placeholder = match[1].trim();
            if (placeholder) {
                placeholders.push(placeholder);
            }
        }

        return [...new Set(placeholders)]; // Remove duplicates
    };

    // Validation
    const validateAggregationConfiguration = () => {
        if (!formData?.type) {
            toast.error('Please select a type');
            return false;
        }

        if (!formData?.tableId) {
            toast.error('Please select a collection');
            return false;
        }

        if (!aggregationQuery.trim()) {
            toast.error('Please enter an aggregation query');
                return false;
        }

        return true;
    };

    // Render Components
    const renderFormSection = () => (
        <div className="form-section">
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Type *</Form.Label>
                <Form.Control
                    as="select"
                    name="type"
                    value={formData?.type || ''}
                    onChange={handleChange}
                    className="form-control-lg"
                >
                    <option value="">Select Type...</option>
                    <option value="AGGREGATION">Aggregation</option>
                    <option value="PIPELINE">Pipeline</option>
                    <option value="QUERY">Query</option>
                </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Response Type *</Form.Label>
                <Form.Control
                    as="select"
                    name="responseType"
                    value={formData?.responseType || ''}
                    onChange={handleChange}
                    className="form-control-lg"
                >
                    <option value="">Select Response Type...</option>
                    <option value="SINGLE">Single</option>
                    <option value="OBJECT">Object</option>
                    <option value="LIST">List</option>
                </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Collection From *</Form.Label>
            <Form.Control 
                as="select" 
                name="tableId" 
                value={formData?.tableId || ''} 
                onChange={handleChange}
                    className="form-control-lg"
            >
                    <option value="">Select Collection From...</option>
                {allTableList?.map((table) => (
                    <option key={table?.id} value={table?.id}>
                        {table?.name}
                    </option>
                ))}
            </Form.Control>
        </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Source of Input</Form.Label>
                <Select
                    isMulti
                    options={[
                        { value: 'custom_request', label: 'Custom Request' },
                        ...allPreviousSteps.map(step => ({
                            value: `step_${step.id}`,
                            label: `Step ${step.id}`
                        }))
                    ]}
                    value={requestParamSource.map(source => {
                        if (source === 'custom_request') {
                            return { value: 'custom_request', label: 'Custom Request' };
                        }
                        const stepMatch = source.match(/step_(\d+)/);
                        if (stepMatch) {
                            return { value: source, label: `Step ${stepMatch[1]}` };
                        }
                        return { value: source, label: source };
                    })}
                    onChange={handleRequestParamSourceChange}
                    placeholder="Select input sources"
                    className="react-select-container"
                />
                {requestParameters.length > 0 && (
                    <Form.Text className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        Available parameters: {requestParameters.map(p => p.label).join(', ')}
                    </Form.Text>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Value *</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={12}
                    name="aggregationQuery"
                    value={aggregationQuery}
                    onChange={handleQueryChange}
                    placeholder={`{
  "$group": {
    "_id": null,
    "totalItems": { "$sum": 1 },
    "itemAbove{priceThreshold}": {
      "$sum": {
        "$cond": [{ "$gt": ["$price", {priceThreshold}] }, 1, 0]
      }
    },
    "customer{clientId}": { "$first": "$customer" },
    "customerName{customer.name}": { "$first": "$customerName" },
    "status{orderStatus}": { "$first": "$status" }
  }
}`}
                    className="font-monospace"
                    style={{ borderColor: '#007bff', fontSize: '14px' }}
                />
                <Form.Text className="text-muted">
                    Use placeholders like {'{name}'}, {'{priceThreshold}'}, {'{customer.name}'} etc. (valid variable names only) that will be replaced with mapped values below.
                </Form.Text>
            </Form.Group>

            {requestParameters.length > 0 && extractPlaceholders(aggregationQuery).length > 0 && (
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Map Table Columns to Request Parameters</Form.Label>
                    
                    {parametersLoading && (
                        <div className="alert alert-info">
                            <i className="fa fa-spinner fa-spin me-2"></i>
                            Loading request parameters...
                        </div>
                    )}
                    
                    {/* Add new mapping section */}
                    <div className="row mb-3">
                        <div className="col-md-8">
                            <Form.Label className="small text-muted">Table Columns</Form.Label>
                        <Select
                                options={extractPlaceholders(aggregationQuery)
                                    .filter(placeholder => !formData?.mappings?.[placeholder])
                                    .map(placeholder => ({
                                        value: placeholder,
                                        label: placeholder
                                    }))}
                                value={selectedPlaceholder ? { value: selectedPlaceholder, label: selectedPlaceholder } : null}
                                onChange={(selected) => setSelectedPlaceholder(selected?.value || null)}
                                placeholder="Select Table Column"
                            className="react-select-container"
                                isDisabled={requestParameters.length === 0 || parametersLoading}
                            />
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleAddMapping}
                                disabled={!selectedPlaceholder || requestParameters.length === 0 || parametersLoading}
                            >
                                Add
                            </Button>
                        </div>
                    </div>

                    {/* Existing mappings */}
                    {Object.keys(formData?.mappings || {}).map((placeholder) => {
                        const mappingValue = formData?.mappings?.[placeholder] || '';
                        const columnAttributeMappings = Object.keys(formData?.mappings || {}).filter(key => key.startsWith(`${placeholder}.`));
                        const hasAttributeMapping = columnAttributeMappings.length > 0;
                        
                        let selectedParameter = null;
                        if (hasAttributeMapping) {
                            const mappingKey = columnAttributeMappings[0];
                            const mappingValue = formData?.mappings?.[mappingKey] || '';
                            const { parameterName } = parseMappingString(mappingValue);
                            selectedParameter = requestParameters.find(param => param.label.split(' ')[0] === parameterName);
                        } else {
                            selectedParameter = requestParameters.find(param => param.value === mappingValue);
                        }

                        const parameterAttributes = getParameterAttributes(selectedParameter);
                        const hasParameterAttributes = parameterAttributes?.length > 0;

                        return (
                            <div key={placeholder} className="card mb-2 border">
                                <div className="card-body py-3">
                                    <div className="row align-items-center">
                                        <div className="col-md-3">
                                            <span className="text-primary fw-bold" style={{ color: '#0d6efd' }}>{placeholder}</span>
                                        </div>
                                        <div className="col-md-4">
                                            <Form.Label className="small text-muted mb-1">Select Parameter</Form.Label>
                        <Select
                                                options={requestParameters}
                                                value={selectedParameter}
                                                onChange={(selected) => handleParameterMappingChange(selected, placeholder)}
                                                placeholder="Select Parameter"
                            className="react-select-container"
                                                isDisabled={requestParameters.length === 0 || parametersLoading}
                        />
                    </div>
                                        {hasParameterAttributes && (
                                            <div className="col-md-3">
                                                <Form.Label className="small text-muted mb-1">Select Attribute</Form.Label>
                                                <Select
                                                    options={parameterAttributes.map(attr => ({
                                                        value: attr.name,
                                                        label: attr.name
                                                    }))}
                                                    value={columnAttributes[placeholder] ? { 
                                                        value: columnAttributes[placeholder], 
                                                        label: columnAttributes[placeholder] 
                                                    } : null}
                                                    onChange={(selected) => handleColumnAttributeChange(selected, placeholder)}
                                                    placeholder="Select Attribute"
                                                    className="react-select-container"
                                                    isDisabled={requestParameters.length === 0 || parametersLoading}
                        />
                    </div>
                                        )}
                                        <div className="col-md-2 text-end">
                    <Button 
                        variant="outline-danger" 
                        size="sm" 
                                                onClick={() => handleRemoveMapping(placeholder)}
                                                style={{ borderColor: '#dc3545', color: '#dc3545' }}
                    >
                        <i className="fa fa-trash"></i>
                    </Button>
                </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {requestParameters.length === 0 && extractPlaceholders(aggregationQuery).length > 0 && !parametersLoading && (
                        <div className="alert alert-info">
                            <i className="fa fa-info-circle me-2"></i>
                            No input parameters available. Please select input sources above to see available parameters for mapping.
                        </div>
                    )}
        </Form.Group>
            )}
        </div>
    );

    const renderPreviewSection = () => (
        <div className="preview-section">
            <h5 className="mb-4">PREVIEW</h5>
            <div className="bg-light p-4 rounded border" style={{ minHeight: '300px' }}>
                {formattedPreview ? (
                    <pre className="mb-0" style={{ fontSize: '13px', color: '#333', lineHeight: '1.4' }}>
                        <code>{formattedPreview}</code>
                    </pre>
                ) : (
                    <div className="text-muted text-center" style={{ marginTop: '100px' }}>
                        <i className="fa fa-code fa-3x mb-3"></i>
                        <p>Enter a query in the Value field to see the preview</p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <Row style={{ minHeight: '600px' }}>
                <Col md={12}>
                    {renderFormSection()}
                </Col>
            </Row>
            <hr style={{ margin: "20px 0 30px" }} />
            
            {/* Collapsible Preview Section */}
            <div className="mb-4">
                <Button
                    variant="outline-primary"
                    onClick={() => setShowPreview(!showPreview)}
                    className="w-100"
                >
                    <i className={`fa fa-chevron-${showPreview ? 'up' : 'down'} me-2`}></i>
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                </Button>
                
                {showPreview && (
                    <div className="mt-3">
                        {renderPreviewSection()}
                    </div>
                )}
            </div>
            
            <hr style={{ margin: "20px 0 30px" }} />
            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );
};

export default AggregationAction; 